import React, { useState, useEffect, useContext } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import "./App.scss";
import ProtectedRoute from "./components/ProtectedRoute";
import analytics from "./services/analytics";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PasswordRecovery from "./pages/PasswordRecovery";
import PasswordReset from "./pages/PasswordReset";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Spinner from "./components/Spinner";
import AccountSettings from "./pages/AccountSettings";
import RiskAssessmentPage from "./pages/RiskAssessment";
import ComingSoonAssessment from "./pages/ComingSoonAssessment";
import Products from "./pages/Products";
import Features from "./pages/Features";
import AboutUs from "./pages/AboutUs";
import Contact from "./pages/Contact";
import ToastNotification from "./components/ToastNotification";
import NotFound from "./pages/NotFound";
import ConfirmAccount from "./pages/ConfirmAccount";
import Profile from "./pages/Profile";
import Dashboard from "./pages/Dashboard";
import { AuthContext } from "./contexts/AuthContext";
import AuthErrorBoundary from "./components/AuthErrorBoundary";

const App = () => {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);

  // Initialize Google Analytics
  useEffect(() => {
    analytics.init();
  }, []);

  // Track page views
  useEffect(() => {
    analytics.pageView(location.pathname + location.search);
  }, [location]);

  // Update plan state based on authentication status
  useEffect(() => {
    if (isAuthenticated) {
      const storedPlan = JSON.parse(
        localStorage.getItem("plan_status") ||
          sessionStorage.getItem("plan_status") ||
          "null"
      );
      setPlan(storedPlan?.plan || null);
    } else {
      // Clear plan when not authenticated
      setPlan(null);
    }
  }, [isAuthenticated]);

  // Listen for storage changes to update plan status (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleStorageChange = () => {
      const storedPlan = JSON.parse(
        localStorage.getItem("plan_status") ||
          sessionStorage.getItem("plan_status") ||
          "null"
      );
      setPlan(storedPlan?.plan || null);
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events when localStorage is updated within the same tab
    window.addEventListener('planStatusUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('planStatusUpdated', handleStorageChange);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 800); // Simulate loading time
    return () => clearTimeout(timer);
  }, [location]);

  const planRouteMap = {
    basic_month: "/products/risk-assessment",
    basic_year: "/products/risk-assessment",
    premium_month: "/products/premium-assessment",
    premium_year: "/products/premium-assessment",
    enterprise_month: "/products/enterprise-assessment",
    enterprise_year: "/products/enterprise-assessment",
  };

  const restrictAccessTo = (allowedPlan, Component) => {
    return (
      <ProtectedRoute>
        {plan === null ? (
          // If plan is null, let the component mount and fetch plan status
          <Component />
        ) : plan?.includes(allowedPlan) ? (
          <Component />
        ) : (
          <Navigate to={planRouteMap[plan] } replace />
        )}
      </ProtectedRoute>
    );
  };

  return (
    <HelmetProvider>
      <AuthErrorBoundary>
        <Spinner loading={loading} />
        <ToastNotification />
        <Navbar />
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route
          path="/home"
          element={ <Home /> }
        />
        <Route path="/auth/signin" element={<Login />} />
        <Route path="/auth/signup" element={<Register />} />
        <Route path="/auth/confirm" element={<ConfirmAccount />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/auth/profile"
          element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          }
        />
        <Route path="/auth/forgot-password" element={<PasswordRecovery />} />
        <Route path="/auth/reset-password" element={<PasswordReset />} />
        <Route
          path="/products"
          element={
              <Products />
          }
        />
        <Route
          path="/products/risk-assessment"
          element={restrictAccessTo("basic", RiskAssessmentPage)}
        />
        <Route
          path="/products/premium-assessment"
          element={<ComingSoonAssessment planName="Premium" />}
        />
        <Route
          path="/products/enterprise-assessment"
          element={<ComingSoonAssessment planName="Enterprise" />}
        />
        <Route
          path="/features"
          element={<Features />}
        />
        <Route
          path="/about"
          element={<AboutUs />}
        />
        <Route
          path="/contact"
          element={<Contact />}
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
        <Footer />
      </AuthErrorBoundary>
    </HelmetProvider>
  );
};

export default App;
